package top.wcpe.allincore.ui.web.mock

import kotlinx.browser.window
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.request.RequestCallbacks
import top.wcpe.allincore.ui.runtime.ir.request.RequestDispatcher
import top.wcpe.allincore.ui.runtime.ir.request.RequestRegistry
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateAccess
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateAccessProvider
import top.wcpe.allincore.ui.runtime.ir.state.SharedStateRegistry

internal typealias MockCallbackPayload = Map<String, Any>

internal interface MockScheduledTask {
    fun cancel()
}

internal fun interface MockTaskScheduler {
    fun schedule(delayMs: Long, task: () -> Unit): MockScheduledTask
}

internal object BrowserMockTaskScheduler : MockTaskScheduler {
    override fun schedule(delayMs: Long, task: () -> Unit): MockScheduledTask =
        BrowserScheduledTask(delayMs, task).also(BrowserScheduledTask::start)

    private class BrowserScheduledTask(delayMs: Long, private val task: () -> Unit) : MockScheduledTask {
        private var remainingMs = delayMs
        private var timerId: Int? = null
        private var cancelled = false

        fun start() {
            scheduleNext()
        }

        override fun cancel() {
            cancelled = true
            timerId?.let(window::clearTimeout)
            timerId = null
        }

        private fun scheduleNext() {
            if (cancelled) return
            val chunk = minOf(remainingMs, MAX_BROWSER_TIMER_DELAY_MS)
            remainingMs -= chunk
            timerId = window.setTimeout({
                timerId = null
                if (cancelled) return@setTimeout
                if (remainingMs > 0L) scheduleNext() else task()
            }, chunk.toInt())
        }
    }
}

internal interface MockCallbackGateway {
    fun deliverSuccess(registry: RequestRegistry, requestId: String, payload: MockCallbackPayload, ctx: EvalContext)

    fun deliverFailure(registry: RequestRegistry, requestId: String, payload: MockCallbackPayload, ctx: EvalContext)

    fun deliverTimeout(registry: RequestRegistry, requestId: String, ctx: EvalContext)
}

internal object RuntimeMockCallbackGateway : MockCallbackGateway {
    override fun deliverSuccess(
        registry: RequestRegistry,
        requestId: String,
        payload: MockCallbackPayload,
        ctx: EvalContext,
    ) {
        RequestCallbacks.deliver(registry, requestId, true, payload, ctx)
    }

    override fun deliverFailure(
        registry: RequestRegistry,
        requestId: String,
        payload: MockCallbackPayload,
        ctx: EvalContext,
    ) {
        RequestCallbacks.deliver(registry, requestId, false, payload, ctx)
    }

    override fun deliverTimeout(registry: RequestRegistry, requestId: String, ctx: EvalContext) {
        RequestCallbacks.deliverTimeout(registry, requestId, ctx)
    }
}

/** 预览端 Mock dispatcher：所有分支都经过同一异步调度与终态落账链路。 */
@Suppress("TooManyFunctions")
internal class MockRequestDispatcher(
    private val scenario: MockRoutes,
    private val sessionId: Long = 0L,
    private val scheduler: MockTaskScheduler = BrowserMockTaskScheduler,
    private val traceSink: MockTraceSink = InMemoryMockTraceSink(),
    private val diagnosticSink: MockDiagnosticSink = ConsoleMockDiagnosticSink,
    private val callbackGateway: MockCallbackGateway = RuntimeMockCallbackGateway,
    override val sharedStateAccess: SharedStateAccess = SharedStateRegistry.global(),
    private val onCallbackDelivered: () -> Unit = {},
    maxTraceEntries: Int = DEFAULT_TRACE_MAX_ENTRIES,
    maxTraceBytes: Long = DEFAULT_TRACE_MAX_BYTES,
) : RequestDispatcher,
    SharedStateAccessProvider {
    private data class CallbackBinding(val registry: RequestRegistry, val ctxProvider: () -> EvalContext)

    private class PendingDispatch(
        val requestId: String,
        val path: String,
        val requestPayload: Map<String, Any?>,
        val resolution: MockResolution,
        var generation: Long,
    ) {
        var scheduledTask: MockScheduledTask? = null
        var terminal: MockTerminalKind? = null
            private set

        fun compareAndSetTerminal(result: MockTerminalKind): Boolean {
            if (terminal != null) return false
            terminal = result
            return true
        }

        fun rollbackTerminal(result: MockTerminalKind) {
            if (terminal == result) terminal = null
        }
    }

    private val ledger = MockTerminalLedger(maxTraceEntries, maxTraceBytes)
    private val pending = linkedMapOf<String, PendingDispatch>()
    private var binding: CallbackBinding? = null
    private var generation = 0L
    private var acceptingCallbacks = true
    private var disposed = false

    init {
        require(maxTraceEntries >= MIN_DISPATCHER_TRACE_ENTRIES) { "Mock 终态账本条数上限过小" }
        require(maxTraceBytes >= MIN_DISPATCHER_TRACE_BYTES) { "Mock 终态账本字节上限过小" }
    }

    fun bind(registry: RequestRegistry, ctxProvider: () -> EvalContext) {
        if (disposed) return
        val existing = binding
        check(existing == null || existing.registry === registry) { "Mock dispatcher 不能跨 session 重新绑定" }
        binding = CallbackBinding(registry, ctxProvider)
    }

    fun freeze() {
        if (disposed || !acceptingCallbacks) return
        acceptingCallbacks = false
        generation += 1L
    }

    fun resume() {
        if (disposed || acceptingCallbacks) return
        generation += 1L
        pending.values.forEach { dispatch ->
            if (dispatch.terminal == null) dispatch.generation = generation
        }
        acceptingCallbacks = true
    }

    override fun send(requestId: String, namespace: String, handler: String, payload: Map<String, Any?>) {
        if (disposed) return
        val canonicalPath = canonicalRequestPath(namespace, handler)
        if (requestId.length > MAX_MOCK_REQUEST_ID_LENGTH) {
            binding?.registry?.take(requestId)
            reportDiagnostic(
                code = MOCK_REQUEST_ID_TOO_LONG,
                message = "Mock requestId 超出可支持长度，已拒绝请求",
                requestId = requestId.take(MAX_DIAGNOSTIC_REQUEST_ID_LENGTH),
                path = canonicalPath.boundedTracePath(),
            )
            return
        }
        val path = canonicalPath.boundedTracePath()
        val resolution = if (canonicalPath.length <= MAX_MOCK_ROUTE_PATH_LENGTH) {
            resolveMockRoute(scenario, canonicalPath, payload)
        } else {
            MockResolution(MockDecision.None, MockMatchLocation.Unregistered)
        }
        val dispatch = PendingDispatch(
            requestId = requestId,
            path = path,
            requestPayload = snapshotPayload(payload),
            resolution = resolution,
            generation = generation,
        )
        pending[requestId] = dispatch
        if (!acceptingCallbacks) {
            completeCancelled(dispatch)
            return
        }
        dispatch.scheduledTask = scheduler.schedule(resolution.decision.delayMs()) {
            completeScheduled(dispatch)
        }
    }

    fun dispose() {
        if (disposed) return
        disposed = true
        acceptingCallbacks = false
        generation += 1L
        pending.values.toList().forEach { dispatch ->
            dispatch.scheduledTask?.cancel()
            completeCancelled(dispatch)
        }
        pending.clear()
        binding = null
        ledger.clearAll()
    }

    fun trace(requestId: String): MockTrace? = ledger.find(sessionId, requestId)

    fun traces(): List<MockTrace> = ledger.records

    fun clearSession(sessionId: Long) {
        ledger.clearSession(sessionId)
        clearSinkSession(sessionId)
    }

    fun clearAll() {
        ledger.clearAll()
        clearSinkAll()
    }

    private fun completeScheduled(dispatch: PendingDispatch) {
        if (disposed || dispatch.generation != generation || !acceptingCallbacks) return
        when (val decision = dispatch.resolution.decision) {
            MockDecision.None -> completeWithCallback(
                dispatch = dispatch,
                result = MockTerminalKind.UNREGISTERED,
                resultPayload = unregisteredPayload(dispatch.path),
                callback = CallbackKind.FAILURE,
            )
            is MockDecision.Success -> completeWithCallback(
                dispatch,
                MockTerminalKind.SUCCESS,
                decision.payload,
                CallbackKind.SUCCESS,
            )
            is MockDecision.Failure -> completeWithCallback(
                dispatch,
                MockTerminalKind.ERROR,
                decision.payload,
                CallbackKind.FAILURE,
            )
            is MockDecision.Timeout -> completeWithCallback(
                dispatch,
                MockTerminalKind.TIMEOUT,
                emptyMap(),
                CallbackKind.TIMEOUT,
            )
        }
    }

    private fun completeWithCallback(
        dispatch: PendingDispatch,
        result: MockTerminalKind,
        resultPayload: MockCallbackPayload,
        callback: CallbackKind,
    ) {
        val callbackBinding = binding ?: run {
            reportDiagnostic(MOCK_CALLBACK_UNBOUND, "Mock 回调上下文尚未绑定", dispatch)
            return
        }
        if (!dispatch.compareAndSetTerminal(result)) return
        dispatch.scheduledTask = null
        val trace = when (val appendResult = appendTrace(dispatch, result, resultPayload, callbackInProgress = true)) {
            is MockLedgerAppendResult.Stored -> appendResult.trace
            MockLedgerAppendResult.TemporarilyFull -> {
                dispatch.rollbackTerminal(result)
                reportDiagnostic(MOCK_TRACE_LEDGER_RETRY, "Mock 终态账本暂不可写，已延后回调", dispatch)
                dispatch.scheduledTask = scheduler.schedule(TRACE_LEDGER_RETRY_DELAY_MS) {
                    completeScheduled(dispatch)
                }
                return
            }
            MockLedgerAppendResult.Oversized -> {
                reportDiagnostic(MOCK_TRACE_METADATA_TOO_LARGE, "Mock 终态元数据超出容量，已拒绝回调", dispatch)
                pending.remove(dispatch.requestId)
                callbackBinding.registry.take(dispatch.requestId)
                return
            }
        }
        recordTerminalTrace(trace, dispatch)
        pending.remove(dispatch.requestId)
        try {
            deliverCallback(callbackBinding, dispatch.requestId, resultPayload, callback)
            notifyCallbackDelivered(dispatch)
        } finally {
            ledger.release(sessionId, dispatch.requestId)
        }
    }

    private fun completeCancelled(dispatch: PendingDispatch) {
        if (!dispatch.compareAndSetTerminal(MockTerminalKind.CANCELLED)) return
        dispatch.scheduledTask?.cancel()
        dispatch.scheduledTask = null
        when (
            val appendResult = appendTrace(
                dispatch = dispatch,
                result = MockTerminalKind.CANCELLED,
                resultPayload = emptyMap(),
                callbackInProgress = false,
            )
        ) {
            is MockLedgerAppendResult.Stored -> recordTerminalTrace(appendResult.trace, dispatch)
            MockLedgerAppendResult.TemporarilyFull ->
                reportDiagnostic(MOCK_TRACE_LEDGER_RETRY, "Mock 取消终态账本暂不可写", dispatch)
            MockLedgerAppendResult.Oversized ->
                reportDiagnostic(MOCK_TRACE_METADATA_TOO_LARGE, "Mock 取消终态元数据超出容量", dispatch)
        }
        pending.remove(dispatch.requestId)
        binding?.registry?.take(dispatch.requestId)
    }

    private fun appendTrace(
        dispatch: PendingDispatch,
        result: MockTerminalKind,
        resultPayload: MockCallbackPayload,
        callbackInProgress: Boolean,
    ): MockLedgerAppendResult = ledger.tryAppend(
        MockTrace(
            sessionId = sessionId,
            requestId = dispatch.requestId,
            path = dispatch.path,
            requestPayload = dispatch.requestPayload,
            match = dispatch.resolution.location,
            delayMs = dispatch.resolution.decision.delayMs(),
            result = result,
            resultPayload = snapshotPayload(resultPayload),
        ),
        callbackInProgress = callbackInProgress,
    )

    private fun deliverCallback(
        callbackBinding: CallbackBinding,
        requestId: String,
        resultPayload: MockCallbackPayload,
        callback: CallbackKind,
    ) {
        val ctx = callbackBinding.ctxProvider()
        when (callback) {
            CallbackKind.SUCCESS -> callbackGateway.deliverSuccess(
                callbackBinding.registry,
                requestId,
                resultPayload,
                ctx,
            )
            CallbackKind.FAILURE -> callbackGateway.deliverFailure(
                callbackBinding.registry,
                requestId,
                resultPayload,
                ctx,
            )
            CallbackKind.TIMEOUT -> callbackGateway.deliverTimeout(
                callbackBinding.registry,
                requestId,
                ctx,
            )
        }
    }

    private fun recordTerminalTrace(trace: MockTrace, dispatch: PendingDispatch) {
        recordToSink(trace, dispatch)
    }

    private fun recordToSink(trace: MockTrace, dispatch: PendingDispatch) {
        try {
            traceSink.record(trace)
        } catch (_: Throwable) {
            reportDiagnostic(MOCK_TRACE_SINK_FAILED, "Mock 轨迹 sink 写入失败", dispatch)
        }
    }

    private fun notifyCallbackDelivered(dispatch: PendingDispatch) {
        try {
            onCallbackDelivered()
        } catch (_: Throwable) {
            reportDiagnostic(MOCK_CALLBACK_HOOK_FAILED, "Mock 回调后的刷新接缝执行失败", dispatch)
        }
    }

    private fun clearSinkSession(sessionId: Long) {
        try {
            traceSink.clearSession(sessionId)
        } catch (_: Throwable) {
            reportDiagnostic(MOCK_TRACE_SINK_FAILED, "Mock 轨迹 sink 清理 session 失败")
        }
    }

    private fun clearSinkAll() {
        try {
            traceSink.clearAll()
        } catch (_: Throwable) {
            reportDiagnostic(MOCK_TRACE_SINK_FAILED, "Mock 轨迹 sink 全量清理失败")
        }
    }

    private fun reportDiagnostic(code: String, message: String, dispatch: PendingDispatch? = null) {
        reportDiagnostic(code, message, dispatch?.requestId, dispatch?.path)
    }

    private fun reportDiagnostic(code: String, message: String, requestId: String?, path: String?) {
        val diagnostic = MockDiagnostic(
            code = code,
            message = message,
            sessionId = sessionId,
            requestId = requestId,
            path = path,
        )
        try {
            diagnosticSink.report(diagnostic)
        } catch (_: Throwable) {
            console.warn("MOCK_DIAGNOSTIC_SINK_FAILED：Mock 诊断 sink 写入失败")
        }
    }

    private enum class CallbackKind {
        SUCCESS,
        FAILURE,
        TIMEOUT,
    }
}

private fun canonicalRequestPath(namespace: String, handler: String): String {
    val cleanNamespace = namespace.trim().trim('.')
    val cleanHandler = handler.trim().trim('.')
    return if (cleanNamespace.isEmpty()) cleanHandler else "$cleanNamespace.$cleanHandler"
}

private fun String.boundedTracePath(): String =
    if (length <= MAX_MOCK_ROUTE_PATH_LENGTH) this else take(MAX_MOCK_ROUTE_PATH_LENGTH - TRACE_PATH_SUFFIX.length) +
        TRACE_PATH_SUFFIX

private fun MockDecision.delayMs(): Long = when (this) {
    MockDecision.None -> 0L
    is MockDecision.Success -> delayMs
    is MockDecision.Failure -> delayMs
    is MockDecision.Timeout -> delayMs
}

private fun unregisteredPayload(path: String): MockCallbackPayload = mapOf(
    "code" to MOCK_ROUTE_NOT_FOUND,
    "message" to MOCK_ROUTE_NOT_FOUND_MESSAGE,
    "path" to path,
)

private const val MAX_BROWSER_TIMER_DELAY_MS = 2_147_483_647L
private const val MIN_DISPATCHER_TRACE_ENTRIES = 2
private const val MIN_DISPATCHER_TRACE_BYTES = 16L * 1024L
private const val MAX_MOCK_REQUEST_ID_LENGTH = 128
private const val MAX_DIAGNOSTIC_REQUEST_ID_LENGTH = 64
private const val TRACE_LEDGER_RETRY_DELAY_MS = 1L
private const val TRACE_PATH_SUFFIX = "..."
